import { useState, useMemo } from 'react';
import { ChevronDown } from 'lucide-react';
import { Input } from '@/components/ui/input';

interface FilterDropdownProps {
  value: string;
  options: string[];
  onChange: (value: string) => void;
  allLabel: string;
  searchPlaceholder?: string;
  noResultsLabel?: string;
  searchable?: boolean;
  capitalizeFirst?: boolean;
  className?: string;
  menuClassName?: string;
}

export const FilterDropdown = ({
  value,
  options,
  onChange,
  allLabel,
  searchPlaceholder = 'Search',
  noResultsLabel = 'No results found',
  searchable = false,
  capitalizeFirst = false,
  className = '',
  menuClassName = '',
}: FilterDropdownProps) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const filteredOptions = useMemo(() => {
    if (!searchable || !query) return options;
    const q = query.toLowerCase();
    return options.filter((o) => o.toLowerCase().includes(q));
  }, [options, query, searchable]);

  const displayLabel = (option: string) => (option === 'All' ? allLabel : option);
  const labelClassName = capitalizeFirst ? 'first-letter:capitalize' : '';

  const select = (option: string) => {
    onChange(option);
    setQuery('');
    setOpen(false);
  };

  return (
    <div className={`relative flex-1 min-w-0 ${className}`}>
      {searchable ? (
        <>
          <Input
            placeholder={searchPlaceholder}
            value={open ? query : displayLabel(value)}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 200)}
            className={`w-full pr-8 text-sm h-8 bg-white focus-visible:ring-0 focus-visible:ring-offset-0 ${labelClassName}`}
          />
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
        </>
      ) : (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          className="flex items-center gap-1.5 h-8 sm:h-11 w-full px-3 rounded-md border border-border shadow-sm bg-white text-sm font-medium text-foreground hover:bg-gray-50 transition-colors justify-between"
        >
          <span className={`truncate ${labelClassName}`}>{displayLabel(value)}</span>
          <ChevronDown
            className={`w-4 h-4 text-muted-foreground transition-transform shrink-0 ${open ? 'rotate-180' : ''}`}
          />
        </button>
      )}

      {open && (
        <div
          className={`absolute top-full left-0 w-full mt-1 bg-background border border-border rounded-md shadow-lg z-50 max-h-48 overflow-y-auto ${menuClassName}`}
        >
          {filteredOptions.length === 0 ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">{noResultsLabel}</div>
          ) : (
            filteredOptions.map((option) => (
              <button
                key={option}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  select(option);
                }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors ${labelClassName} ${
                  value === option ? 'bg-primary/10 text-primary font-semibold' : ''
                }`}
              >
                {displayLabel(option)}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};
